import React, { useCallback, useState, useRef, useEffect } from 'react'
import {
  Box,
  Flex,
  Text,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Portal,
  Select,
  Tag,
  Spinner,
} from '@chakra-ui/react'
import { SearchLgIcon, XCloseIcon } from '../../../assets/icons'
import FastAvatar from '../../image/FastAvatar'
import { CircleFlag } from 'react-circle-flags'
import { useTranslation } from 'react-i18next'

interface ModalSelectProps {
  label?: string
  name: string
  value: string
  isInvalid?: boolean
  w?: string
  h?: string
  options: { value: string | null; label: string; countryCode?: string; profilePicture?: string }[]
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  showCountryFlag?: boolean
  isDisabled?: boolean
  placeholder?: string
  searchDisabled?: boolean
  hasProfilePicture?: boolean
  /** @deprecated ModalSelect now supports infinite scroll; pagination controls are no longer rendered. */
  isPaginated?: boolean
  /** Message to show when no search has been entered and there are no options yet. */
  emptyPrompt?: string
  /** @deprecated */
  totalPages?: number
  /** @deprecated */
  currentPage?: number
  /** @deprecated */
  onPageChange?: (page: number) => void
  isLoading?: boolean
  /** Infinite scroll */
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  /** @deprecated */
  hasNextPage?: boolean
  isTop?: boolean
  scrollLockTargetRef?: React.RefObject<HTMLElement>
  onSearch?: (searchTerm: string) => void
  required?: boolean
  /** Compact mode — hides label, shows only the flag as selected value */
  compact?: boolean
  /** Hide the clear (X) button even when a value is selected */
  hideClear?: boolean
}

const ModalSelect: React.FC<ModalSelectProps> = ({
  label,
  name,
  value,
  w,
  h,
  options,
  onChange,
  showCountryFlag = false,
  isDisabled,
  placeholder,
  isInvalid,
  searchDisabled = false,
  hasProfilePicture = false,
  isLoading = false,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isTop = false,
  onSearch,
  emptyPrompt,
  required,
  compact = false,
  hideClear = false,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const placeholderText = placeholder ?? t('common.placeholders.selectOption')
  const emptyPromptText = emptyPrompt ?? t('common.labels.noOptionsFound')
  const showCompactText = compact && !showCountryFlag

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleScroll = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
        return
      }
      setIsOpen(false)
    }
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  // Prevent scroll chaining / background scroll while dropdown is open.
  useEffect(() => {
    if (!isOpen) return

    const body = document.body
    const html = document.documentElement

    const countAttr = 'data-filtermodal-scroll-lock-count'
    const prevOverflowAttr = 'data-filtermodal-prev-overflow'
    const prevPaddingAttr = 'data-filtermodal-prev-padding-right'

    const count = Number(body.getAttribute(countAttr) ?? '0')
    if (count === 0) {
      body.setAttribute(prevOverflowAttr, body.style.overflow || '')
      body.setAttribute(prevPaddingAttr, body.style.paddingRight || '')

      const scrollbarWidth = window.innerWidth - html.clientWidth
      body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`
    }
    body.setAttribute(countAttr, String(count + 1))

    return () => {
      const nextCount = Number(body.getAttribute(countAttr) ?? '1') - 1
      if (nextCount <= 0) {
        const prevOverflow = body.getAttribute(prevOverflowAttr) ?? ''
        const prevPaddingRight = body.getAttribute(prevPaddingAttr) ?? ''

        body.style.overflow = prevOverflow
        body.style.paddingRight = prevPaddingRight

        body.removeAttribute(countAttr)
        body.removeAttribute(prevOverflowAttr)
        body.removeAttribute(prevPaddingAttr)
      } else {
        body.setAttribute(countAttr, String(nextCount))
      }
    }
  }, [isOpen])

  const selectedOption = options.find((opt) => opt.value === value)

  const isRemoteSearch = Boolean(onSearch)

  const filteredOptions = isRemoteSearch
    ? options
    : search
        ? options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
        : options

  const handleSelectOption = (selectedValue: string | null) => {
    const event = {
      target: {
        name,
        value: selectedValue,
      },
    } as React.ChangeEvent<HTMLSelectElement>
    onChange(event)
    setIsOpen(false)
    setSearch('')
  }

  const handleSearch = (newSearch: string) => {
    setSearch(newSearch)
    // Always notify parent about search changes so callers can trigger remote fetches.
    if (onSearch) onSearch(newSearch)
  }

  const handleListScroll = useCallback(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return
    const el = dropdownRef.current
    if (!el) return

    // Find the scroll container inside the dropdown.
    const list = el.querySelector('[data-modal-select-scroll]') as HTMLDivElement | null
    if (!list) return

    const { scrollTop, scrollHeight, clientHeight } = list
    const threshold = 60
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      onLoadMore()
    }
  }, [onLoadMore, hasMore, isLoadingMore])

  const containerWidth = w ?? (compact ? 'auto' : '100%')
  const controlMinHeight = h ?? (compact ? '34px' : '40px')
  const showClear = selectedOption && !isDisabled && !required && !hideClear
  const overlayPaddingRight = showClear ? '52px' : '36px'

  return (
    <Box width={containerWidth} zIndex={1}>
      {label && !compact && (
        <Text textStyle="l-xs" color={required ? 'solid.700' : 'solid.500'} fontWeight={required ? '600' : undefined} mb={1}>
          {label}
          {required && <Text as="span" color="primary.100"> *</Text>}
        </Text>
      )}
      <Box borderRadius="6px" position="relative" ref={ref} bg={required ? 'primary.0' : undefined}>
        <Box w="100%" ref={triggerRef} position="relative">
          <Flex pos="absolute" h="100%" w="100%" alignItems="center" p={`6px ${overlayPaddingRight} 6px 12px`} gap="12px" pointerEvents="none">
            {showCountryFlag && selectedOption?.countryCode && (
              <CircleFlag
                countryCode={selectedOption.countryCode}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  marginRight: '8px',
                }}
              />
            )}
            {placeholderText && !selectedOption && (
              <Box flex="1" minW="0" overflow="hidden">
                <Text textStyle="l-s" color="solid.300" noOfLines={1}>
                  {placeholderText}
                </Text>
              </Box>
            )}
            {showCompactText && selectedOption && (
              <Box flex="1" minW="0" overflow="hidden">
                <Text textStyle="l-s" color="solid.900" noOfLines={1}>
                  {selectedOption.label}
                </Text>
              </Box>
            )}
            {!compact && (hasProfilePicture && selectedOption ? (
              <Box flex="1" minW="0" overflow="hidden">
                <Tag h="28px" borderRadius="14px" gap="8px" bg="solid.50" maxW="100%">
                  <FastAvatar src={selectedOption?.profilePicture || undefined} boxSize="20px" name={selectedOption?.label || ''} />
                  <Text textStyle="l-s" noOfLines={1}>
                    {selectedOption?.label}
                  </Text>
                </Tag>
              </Box>
            ) : (
              <Box flex="1" minW="0" overflow="hidden">
                <Text textStyle="l-s" color={selectedOption ? 'solid.900' : 'solid.400'} noOfLines={1}>
                  {selectedOption?.label}
                </Text>
              </Box>
            ))}
          </Flex>
            <Select
            pos="relative"
            bg="transparent"
            variant={'forms'}
            cursor="pointer"
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              setIsOpen(!isOpen)
            }}
            isDisabled={isDisabled}
            color="solid.300"
            _placeholder={{ color: 'solid.300' }}
            sx={{
              color: 'solid.500',
            }}
            value=""
            borderColor={isInvalid ? 'error.100' : required ? 'primary.50' : 'solid.100'}
              borderRadius="6px"
            minH={controlMinHeight}
            style={{ color: 'solid.500' }}
          ></Select>
          {selectedOption && !isDisabled && !required && !hideClear && (
            <Flex
              pos="absolute"
              right="32px"
              top="50%"
              transform="translateY(-50%)"
              alignItems="center"
              justifyContent="center"
              boxSize="20px"
              borderRadius="full"
              cursor="pointer"
              _hover={{ bg: 'solid.50' }}
              onClick={(e) => {
                e.stopPropagation()
                handleSelectOption('')
              }}
              zIndex={1}
              role="group"
            >
              <Icon width="12px" height="12px" color="solid.400" _groupHover={{ color: 'error.100' }}>
                <XCloseIcon />
              </Icon>
            </Flex>
          )}
        </Box>

        {isOpen && !isDisabled && (
          <Portal>
            <Box
              ref={dropdownRef}
              data-modal-select-dropdown="true"
              position="fixed"
              left={triggerRef.current?.getBoundingClientRect().left}
              top={
                !isTop
                  ? triggerRef.current
                    ? triggerRef.current.getBoundingClientRect().top + (triggerRef.current.offsetHeight || 0) + 5
                    : undefined
                  : undefined
              }
              bottom={
                isTop ? window.innerHeight - (triggerRef.current?.getBoundingClientRect().top || 0) + 5 : undefined
              }
              width={triggerRef.current?.offsetWidth}
              minH="120px"
              bg="white"
              zIndex={20000}
              borderRadius="8px"
              borderColor="solid.100"
              boxShadow="0px 2px 8px -2px #00000014, 0px 2px 14px -3px #0000001F"
              borderWidth="1px"
              minW="200px"
              p={0}
            >
              {!searchDisabled && (
                <>
                  <Box px={3} pt={2} pb={2}>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none" h="full">
                        <Icon width="16px" height="16px" fill="none" color="solid.300">
                          <SearchLgIcon />
                        </Icon>
                      </InputLeftElement>
                      <Input
                        variant="filterSearch"
                        placeholder={t('common.labels.searchPlaceholder')}
                        fontSize="14px"
                        borderRadius="6px"
                        bg="solid.25"
                        h="34px"
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        autoFocus
                      />
                    </InputGroup>
                  </Box>
                  <Divider borderColor="solid.50" my={0} />
                </>
              )}
              <Box
                data-modal-select-scroll
                maxH="200px"
                overflowY="auto"
                px={2}
                pt={1}
                pb={1}
                sx={{ overscrollBehavior: 'contain' }}
                onScroll={onLoadMore ? handleListScroll : undefined}
              >
                {isLoading ? (
                  <Flex justify="center" align="center" height="100px">
                    <Spinner color="primary.100" size="sm" />
                  </Flex>
                ) : filteredOptions.length === 0 ? (
                  <Text textStyle="l-xs" color="solid.300" textAlign="center" px={2} py={2}>
                    {search ? t('common.labels.noOptionsFound') : emptyPromptText}
                  </Text>
                ) : (
                  <>
                    {filteredOptions.map((option) => (
                      <Flex
                        key={option.value}
                        alignItems="center"
                        gap="12px"
                        px={2}
                        py="7px"
                        borderRadius="6px"
                        cursor="pointer"
                        transition="background 0.15s ease"
                        _hover={{ bg: 'solid.25' }}
                        onClick={() => handleSelectOption(option.value)}
                      >
                        {showCountryFlag && option.countryCode && (
                          <CircleFlag
                            countryCode={option.countryCode}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                            }}
                          />
                        )}
                        {hasProfilePicture && <FastAvatar src={option.profilePicture || undefined} boxSize="20px" name={option.label || ''} />}
                        <Text textStyle="l-xs" color="solid.700" noOfLines={1}>
                          {option.label}
                        </Text>
                      </Flex>
                    ))}
                    {onLoadMore && hasMore && isLoadingMore && (
                      <Flex justify="center" py={2}>
                        <Spinner size="sm" color="solid.400" />
                      </Flex>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Portal>
        )}
      </Box>
    </Box>
  )
}

export default ModalSelect
